import Head from 'next/head';
import { useRouter } from 'next/router';
import getConfig from 'next/config';

import Layout from '../../components/Layout';
import Hero from '../../components/Home/Hero';
import BoxHeading from '../../components/UI/BoxHeading';
import SquarePattern from '../../components/UI/SquarePattern';
import LargeCards from '../../components/UI/LargeCards';
import SmallCards from '../../components/UI/SmallCards';
import Testimonial from '../../components/UI/Testimonial';
import TitleText from '../../components/UI/TitleText';
import Calculator from '../../components/DemandLP/SalaryCalculator/Calculator';

import metaRobots from '../../helpers/metaRobots';

import sharecardImage from '../../public/images/sharecard.jpg';
import { Box, Container } from '@chakra-ui/react';
import sitePath from '../../helpers/sitePath';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

const { publicRuntimeConfig } = getConfig();
const { talentHubOnboardingUrl } = publicRuntimeConfig;
const hero = {
  longText: false,
  smallText: false,
  title: {
    main: "Talent isn't limited to",
    highlight: 'one zip code',
  },
  text: 'Tap into top global hotspots to quickly scale your engineering team.',
  button: {
    text: 'Get Started',
    url: talentHubOnboardingUrl,
  },
  image: {
    sourceUrl: '/images/hiring-markets/overview-hero.svg',
    altText: 'Engineers from Canada, Latin America and Mexico watch a meeting virtually',
  },
};
const largeCardsTitle = 'Top international talent hubs for hiring';
const largeCards = [
  {
    image: {
      sourceUrl: '/images/globes/canada.svg',
      altText: 'Globe with pin marking Canada',
    },
    title: 'Canada',
    text: 'A premier global market for hiring top-tier engineers, Canada has innovative tech culture and thriving talent ecosystems in Vancouver, Toronto, and Kitchener-Waterloo.',
    button: {
      type: 'primary',
      text: {
        normal: '',
        strong: 'Learn More',
      },
      url: '/hire-developers/canada',
    },
  },
  {
    image: {
      sourceUrl: '/images/globes/latam.svg',
      altText: 'Globe with pins marking Mexico, Chile and Colombia',
    },
    title: 'Latin America',
    text: 'Widely considered the next epicenter for international tech hiring, LatAm has an abundant population of skilled engineers in markets including Chile, Colombia, and Mexico.',
    button: {
      type: 'primary',
      text: {
        normal: '',
        strong: 'Learn More',
      },
      url: '/hire-developers/latam-latin-america',
    },
  },
  {
    image: {
      sourceUrl: '/images/globes/europe.svg',
      altText: 'Globe with pin marking Poland and Spain',
    },
    title: 'Europe',
    text: 'With talented, senior engineers, a dynamic startup marketplace and English proficiency, Poland and Spain are the perfect hiring markets for your tech team.',
    button: {
      type: 'primary',
      text: {
        normal: '',
        strong: 'Learn More',
      },
      url: '/hire-developers/europe',
    },
  },
];

const smallCards = [
  {
    image: {
      sourceUrl: '/images/hiring-markets/time-zone-alignment.svg',
      altText:
        'Slack messages from Canadian, Latin American and Mexican engineers aligned across time zones',
    },
    title: 'Time zone alignment',
    text: 'Build your team with engineers from dynamic hubs that allow for better team collaboration – your hires will overlap with your local team by 3 hours or more.',
  },
  {
    image: {
      sourceUrl: '/images/hiring-markets/engineering-talent-hubs.svg',
      altText: 'Three engineers look at computer together',
    },
    title: 'Engineering talent hubs',
    text: 'Access hiring markets chosen strategically based on the availability of qualified engineers, their specialized skill sets, and their experience with key tech stacks.',
  },
  {
    image: {
      sourceUrl: '/images/hiring-markets/cultural-understanding.svg',
      altText: 'Chat bubble with thumbs up and smiley face',
    },
    title: 'Cultural understanding',
    text: 'Count on Terminal’s localized hiring experts to spot great talent - they understand the nuances in interview and workplace culture within each specific global market.',
  },
];

const testimonial = {
  bgDark: false,
  image: {
    sourceUrl: '/images/testimonials/andrew-backes.jpg',
    altText: 'Andrew Backes',
  },
  text: '“Being able to hire in other markets is a huge benefit of Terminal. It takes a long time and substantial effort to build a business presence in a different market like Mexico or Canada. Now we have engineers on the team that feel like employees and not contractors. They’re included in the company just as much as anyone else and the experience is seamless.”',
  author: {
    name: 'Andrew Backes',
    jobTitle: 'Head of Engineering at Armory',
  },
};

const titleText = {
  title: 'Why hire outside of the US?',
  text: 'When you team up with Terminal to hire globally, you access a pre-vetted pool of experienced engineering talent to grow your business fast. By building a remote team, you avoid the costs, complexity, and time it takes to relocate engineers to the US or set up a business presence in another country. Engineers love it, too. As remote-first culture shapes the future of work, remote employers can benefit from happier teams and increased retention.',
};

const calculatorTheme = {
  green100: 'green.100',
  green200: 'green.500',
  green300: 'grey.900',
};

const footer = {
  longText: true,
  text: 'Learn more about how you can tap into global markets with Terminal',
};

export async function getServerSideProps({ locale }) {
  return {
    props: {
      ...(await serverSideTranslations(locale)),
    },
  };
}

export default function HiringMarkets() {
  const router = useRouter();

  const meta = {
    title: 'Terminal: Where we hire - Terminal.io',
    description:
      'Talent isn’t limited to one zip code. Tap into global hotspots in Canada, Mexico and Latin America to build your team.',
  };

  return (
    <>
      <Head>
        <link rel="canonical" href={sitePath(router.asPath)} />
        <title>{meta.title}</title>
        <meta property="og:title" content={meta.title} />
        <meta name="description" content={meta.description} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={sitePath(router.asPath)} />
        <meta property="og:image" content={sharecardImage.src} />
        <meta name="robots" content={metaRobots({ translated: false, locale: router.locale })} />
      </Head>
      <Layout type="businesses" footer={footer} withCTA>
        <Box bgColor="grey.100">
          <Hero hero={hero} type="businesses" />
        </Box>
        <SquarePattern
          mt={{ base: '0.25rem', lg: '0.75rem' }}
          mb={{ base: '-18.4375rem', lg: '-14.25rem' }}
        />
        <BoxHeading
          title="Best-in-class engineering talent worldwide"
          color="grey.900"
          maxW="49.5rem"
        />
        {smallCards && smallCards.length && <SmallCards cards={smallCards} />}
        {largeCards && largeCards.length && (
          <LargeCards title={largeCardsTitle} cards={largeCards} size={3} />
        )}
        {testimonial && (
          <>
            <Container mt={{ base: '14', lg: '24' }}>
              <Testimonial
                testimonial={testimonial}
                pt="16"
                pb="20"
                px={{ base: '16', lg: '20' }}
              />
            </Container>
            <SquarePattern mt={{ base: '-18.75rem', lg: '-14.5rem' }} />
          </>
        )}
        {titleText && <TitleText titleText={titleText} />}
        <Box mt={{ base: '10', lg: '24' }}>
          <Calculator theme={calculatorTheme} withStart={false} rounded={false} />
        </Box>
      </Layout>
    </>
  );
}
